import {
	AfterViewInit,
	Component,
	ElementRef,
	Input,
	NgZone,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { ApplicationService } from '@lmc-app/core/services/application.service';
import { GeneralObjects } from '@lmc-models/globalObjects';
import {
	FormBuilder,
	FormControl,
	FormGroup,
	Validators,
} from '@angular/forms';
import { environment } from '@lmc-env/environment';
import {
	MAT_MOMENT_DATE_ADAPTER_OPTIONS,
	MomentDateAdapter,
} from '@angular/material-moment-adapter';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
} from '@angular/material/core';
import { CUSTOM_DATE_FORMAT } from '@lmc-app/shared/constants/common.constant';
import { isExternalUser, User } from '@lmc-app/shared/interface/user.interface';
import { ReplaySubject, Subject, Subscription, takeUntil } from 'rxjs';
import { MatSelectSearchUtil } from '@lmc-app/utils/mat-select-search.util';
import { MatDialog } from '@angular/material/dialog';
import { CreateNoteModalComponent } from './create-note-modal/create-note-modal.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';

@Component({
	selector: 'app-notes',
	templateUrl: './notes.component.html',
	styleUrls: ['./notes.component.scss'],
	providers: [
		{
			provide: DateAdapter,
			useClass: MomentDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
		},
		{ provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMAT },
	],
})
export class NotesComponent
	implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	protected noteFormGroup: FormGroup;

	protected callAttempts = new GeneralObjects();

	private readonly pageSize = 25;
	protected noteNumber = 0;

	// Note data properties
	protected notes: any[] = [];
	protected isLoadingMore = false;
	private hasMore = false;
	private notesSubscription: Subscription;

	// Loads the next batch when scrolled into view
	@ViewChild('loadMoreSentinel') private loadMoreSentinel: ElementRef<HTMLElement>;
	private scrollObserver: IntersectionObserver;
	protected subjects: { note_subject_id: number, subject: string, description: string }[] = [];

	// Note Email Toggle notification
	private isEmail = false;
	protected officeEmails: any;
	protected selectedEmail = '';
	private serverURL: string = environment.iframeURL;
	protected isLoading: boolean = false;

	@Input() currentUser: User;
	@Input() application: any;
	@Input() applicationId: string;
	protected callAttemptFlag = false;
	// Search functionality for note subjects
	protected noteSubjectsFilter: FormControl<string> = new FormControl<string>('', {
		nonNullable: true,
	});
	protected filteredNoteSubjects: ReplaySubject<any[]> = new ReplaySubject<any[]>(
		1
	);
	protected readonly isExternalUser = isExternalUser;
	protected _onDestroy = new Subject<void>();

	constructor(
		private readonly apiService: LegacyApiService,
		private readonly applicationService: ApplicationService,
		private readonly dialog: MatDialog,
		private readonly snackBar: MatSnackBar,
		private readonly ngZone: NgZone,
		private readonly formBuilder: FormBuilder
	) {
		this.noteFormGroup = this.formBuilder.group({
			subject: ['', Validators.required],
			description: ['', [Validators.required, Validators.maxLength(250)]],
			communication: ['INT', Validators.required],
			note: [''],
			RLI_Called_Count: [''],
			email_notes: [''],
			email_address: [''],
		});
	}

	public ngOnInit() {
		this.getNoteSubjects();
		this.noteSubjectsFilter.valueChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe((searchValue) => {
				MatSelectSearchUtil.filterAllStringFields(
					this.subjects,
					searchValue,
					this.filteredNoteSubjects
				);
			});
	}

	public async ngOnChanges(changes: SimpleChanges) {
		if (changes.applicationId?.currentValue) {
			this.applicationId = changes.applicationId?.currentValue;
			this.loadNoteNumber();
			this.loadNotes();
		}
		if (changes.application?.currentValue) {
			this.application = changes.application?.currentValue;
			const originalNoteNumber = this.noteNumber;
			await this.loadNoteNumber();
			if (originalNoteNumber !== this.noteNumber) {
				this.refreshNotes();
			}
		}
		if (changes.currentUser?.currentValue) {
			this.currentUser = changes.currentUser.currentValue;
			if (isExternalUser(this.currentUser)) {
				this.noteFormGroup.disable();
			} else {
				this.noteFormGroup.enable();
			}
		}
	}

	private async loadNoteNumber() {
		const res = await firstValueFrom(this.apiService.getNoteNumberLocalDB(this.applicationId, this.isAdmin()));
		if (res.message === 'Success') {
			this.noteNumber = res.data[0].note_number;
		}
	}

	public ngAfterViewInit() {
		this.scrollObserver = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					this.ngZone.run(() => this.loadMoreNotes());
				}
			},
			{ rootMargin: '200px' }
		);
		this.scrollObserver.observe(this.loadMoreSentinel.nativeElement);
	}

	// Loads the first batch, replacing the list
	private loadNotes() {
		this.isLoading = true;
		this.noteFormGroup.get('communication').setValue('INT');
		this.fetchNotes(this.pageSize, (rows) => {
			this.notes = rows;
		});
	}

	// Re-fetches every loaded note in place, keeping the scroll position
	private refreshNotes() {
		const loadedPages = Math.max(Math.ceil(this.notes.length / this.pageSize), 1);
		this.fetchNotes(loadedPages * this.pageSize, (rows) => {
			this.notes = rows;
		});
	}

	// Appends the next batch
	private loadMoreNotes() {
		if (!this.hasMore || this.isLoading || this.isLoadingMore) {
			return;
		}
		this.isLoadingMore = true;
		const nextPage = this.notes.length / this.pageSize + 1;
		this.notesSubscription = this.apiService
			.getNoteLocalDB(this.applicationId, nextPage, this.pageSize, this.isAdmin())
			.subscribe((res) => {
				this.isLoadingMore = false;
				if (res.message === 'Success') {
					const rows = this.maskNotes(res.data);
					this.notes = [...this.notes, ...rows];
					this.hasMore = rows.length === this.pageSize;
					this.checkSentinel();
				}
			});
	}

	private fetchNotes(size: number, onLoaded: (rows: any[]) => void) {
		this.notesSubscription?.unsubscribe();
		this.isLoadingMore = false;
		this.notesSubscription = this.apiService
			.getNoteLocalDB(this.applicationId, 1, size, this.isAdmin())
			.subscribe((res) => {
				this.isLoading = false;
				if (res.message === 'Success') {
					const rows = this.maskNotes(res.data);
					onLoaded(rows);
					this.hasMore = rows.length === size;
					this.checkSentinel();
				}
			});
	}

	// Re-observing fires the callback again if the sentinel is still in view
	private checkSentinel() {
		const sentinel = this.loadMoreSentinel?.nativeElement;
		if (this.scrollObserver && sentinel) {
			setTimeout(() => {
				this.scrollObserver.unobserve(sentinel);
				this.scrollObserver.observe(sentinel);
			});
		}
	}

	private maskNotes(rows: any[]): any[] {
		if (isExternalUser(this.currentUser)) {
			for (const element of rows) {
				if (
					element.note_subject === 'Fund Notes' ||
					element.note.toLowerCase().includes('saha')
				) {
					element.note = '-';
				}
			}
		}
		return rows;
	}

	protected deleteNote(id: any) {
		if (confirm('Are you sure you want to delete the Note?')) {
			this.apiService.deleteNoteLocalDB(id).subscribe((res) => {
				if (res.message == 'Success') {
					this.snackBar.open('Note deleted successfully!', 'Close', {
						duration: 3000,
						panelClass: ['success-snackbar'],
					});
					this.loadNoteNumber();
					this.refreshNotes();
					this.resetNote();
				}
			});
		}
	}

	protected changeNoteStatus(id: any) {
		if (confirm('Are you sure you want to delete the Note?')) {
			this.apiService
				.changeNoteStatus(
					id,
					'Deleted',
					this.application.id,
					this.currentUser.full_name,
					this.currentUser.zoho_owner_id
				)
				.subscribe(() => {
					this.loadNoteNumber();
					this.refreshNotes();
				});
		}
	}

	protected togglePin(item: any) {
		const pinned = !item.pinned_at;
		this.applicationService
			.setApplicationNotePinned(this.applicationId, item.app_note_id, pinned)
			.subscribe({
				next: () => {
					this.snackBar.open(pinned ? 'Note pinned to top' : 'Note unpinned', 'Close', {
						duration: 3000,
						panelClass: ['success-snackbar'],
					});
					this.refreshNotes();
				},
				error: () => {
					this.snackBar.open(`Failed to ${pinned ? 'pin' : 'unpin'} note`, 'Close', {
						duration: 5000,
						panelClass: ['error-snackbar'],
					});
				},
			});
	}

	protected openNoteModal(
		noteId?: string,
		subject?: string,
		description?: string,
		remark?: string
	) {
		const dialogRef = this.dialog.open(CreateNoteModalComponent, {
			width: '600px',
			data: {
				noteId,
				subject,
				description,
				remark,
				application: this.application,
				currentUser: this.currentUser,
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			// If saved successfully, reload notes
			if (result?.success) {
				this.loadNoteNumber();
				this.refreshNotes();
				this.resetNote();
			}
		});
	}

	protected editNote(item: any) {
		// Fetch the note data from API
		this.isLoading = true;
		this.apiService
			.getIndividualNoteLocalDB(item.app_note_id)
			.subscribe((res) => {
				this.isLoading = false;
				const noteData = res.data[0];

				// Populate the main form with subject and description
				this.noteFormGroup.patchValue({
					subject: noteData.note_subject,
					description: noteData.description,
				});

				// Open modal with all data from API
				this.openNoteModal(
					item.app_note_id,
					noteData.note_subject,
					noteData.description,
					noteData.note
				);
			});
	}

	private getNoteSubjects() {
		this.apiService.getNoteSubjects().subscribe((res: { message: string, data: { note_subject_id: number, subject: string, description: string }[], code: any, status: boolean, fullResponse: { recordsets: { note_subject_id: number, subject: string, description: string }[], recordset: { note_subject_id: number, subject: string, description: string }[], output: any, rowsAffected: number[] }, sql: string }) => {
			if (res['status']) {
				this.subjects = res.data.filter(({ subject }) => subject !== "EC Completed");
				this.filteredNoteSubjects.next(this.subjects.slice());
			}
		});
	}

	protected onchangeNoteSubject() {
		const searchIndex = this.subjects.findIndex(
			(sub) => sub.subject == this.noteFormGroup.get('subject').value
		);
		this.noteFormGroup.patchValue({
			description: this.subjects[searchIndex]['description'],
		});
		this.callAttemptFlag =
			this.noteFormGroup.get('subject').value === 'Loan Interview Attempt';

		this.displayEmails();
	}

	protected async saveNote() {
		this.isLoading = true;
		await this.emailNotes();
		this.apiService
			.createNoteLocalDB(
				this.noteFormGroup.value,
				this.application.Deal_Name,
				this.application.id,
				this.currentUser.full_name,
				this.currentUser.zoho_owner_id,
				this.isEmail,
				this.selectedEmail,
			)
			.subscribe((res) => {
				this.isLoading = false;
				if (res.message == 'Success') {
					let app_note_id = res['data'][0]['app_note_id'] ?? '';

					this.selectedEmail = '';
					this.isEmail = false;
					// Push to ZOHO
					this.apiService
						.addLoanInterviewNotesZOHO(
							this.noteFormGroup.value,
							this.application.id,
							this.currentUser.zoho_owner_id
						)
						.subscribe((res) => {
							if (res['message']['statusCode'] === 201) {
								let zoho_note_id = res['app_id'];
								this.apiService
									.updateNoteIDLocal(app_note_id, zoho_note_id);

								if (this.callAttemptFlag) {
									// Making a object for RLI_Called_Count in order to use same API as Application
									let callObject = {
										RLI_Called_Count:
											this.noteFormGroup.get('RLI_Called_Count').value,
									};
									this.apiService
										.updateZohoApplicationInterface(
											callObject,
											this.application.id
										);
								}

								this.snackBar.open('Note saved successfully!', 'Close', {
									duration: 3000,
									panelClass: ['success-snackbar'],
								});
								this.loadNoteNumber();
								this.refreshNotes();
								this.resetNote();
							} else {
								this.apiService
									.emailNotesToIT(
										this.noteFormGroup.value,
										this.application.id,
										this.application.Deal_Name,
										this.currentUser.full_name
									);
								console.log(res, 'ZOHO Note push failed!');
								this.snackBar.open('Failed to save note to ZOHO', 'Close', {
									duration: 5000,
									panelClass: ['error-snackbar'],
								});
							}
						});
				} else {
					this.snackBar.open('Failed to save note!', 'Close', {
						duration: 5000,
						panelClass: ['error-snackbar'],
					});
					console.log(res, 'Platform: ZOHO Note save failed!');
				}
			});
	}

	get isSaveDisabled(): boolean {
		const subject = this.noteFormGroup.get('subject')?.value;
		const description = this.noteFormGroup.get('description')?.value;
		return !subject || !description;
	}

	private resetNote() {
		this.noteFormGroup.reset({
			subject: '',
			description: '',
			RLI_Called_Count: '',
			communication: 'INT',
			note: '',
			email_notes: '',
			email_address: '',
		});

		Object.keys(this.noteFormGroup.controls).forEach(key => {
			const control = this.noteFormGroup.get(key);
			control?.setErrors(null);
			control?.markAsUntouched();
			control?.markAsPristine();
		});

		this.callAttemptFlag = false;
	}

	private displayEmails() {
		const validators = [Validators.required]; // Validator
		if (this.noteFormGroup.get('subject').value === 'Email Notes') {
			this.noteFormGroup.get('email_address').addValidators(validators); // Add Validator on Email when toggle selected
			this.apiService.getOfficeEmails().subscribe((res) => {
				this.officeEmails = res?.['data'];
			});
		} else {
			this.selectedEmail = ''; // Clear the email address selected when toggle unselected
			this.noteFormGroup.get('email_address').removeValidators(validators); // Remove Validator on Email when toggle unselected
		}
		this.noteFormGroup.get('email_address').updateValueAndValidity(); // Setting the validators based on the above
	}

	private async emailNotes() {
		return new Promise((resolve) => {
			if (
				this.noteFormGroup.get('subject').value === 'Email Notes' &&
				this.selectedEmail !== ''
			) {
				this.apiService
					.toggleEmailNotes(
						this.noteFormGroup.get('note').value,
						this.application.id,
						this.application.Deal_Name,
						this.currentUser.email,
						this.selectedEmail,
						this.serverURL,
						'summary',
						this.currentUser.full_name
					)
					.subscribe((res) => {
						this.isEmail = true;
						resolve(true);
					});
			} else {
				this.isEmail = false;
				this.selectedEmail = '';
				resolve(true);
			}
		});
	}

	// Material Table helper methods
	protected getDummyNotesDataSource(): any[] {
		return Array.from({ length: 4 }, (_, i) => ({
			created: null,
			note: null,
			modified: null,
			status: null,
			actions: null,
		}))
	}

	private isAdmin(): boolean {
		return this.currentUser?.roleID === 0 || this.currentUser?.roleID === 1;
	}

	protected getDisplayedColumns(): string[] {
		if (this.isAdmin()) {
			return ['created', 'note', 'modified', 'status', 'actions'];
		} else {
			return ['created', 'note', 'actions'];
		}
	}

	public ngOnDestroy() {
		this.scrollObserver?.disconnect();
		this.notesSubscription?.unsubscribe();
		this._onDestroy.next();
		this._onDestroy.complete();
	}
}
